import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { MessageCircle, RefreshCw, Smartphone } from 'lucide-react';

const isElectron = () => !!(window.electronAPI?.isElectron);
const PROXY = `${window.location.origin}/api/wa-proxy`;

export function WhatsAppWebView() {
  const [desktopMode] = useState(isElectron());
  const [loading, setLoading] = useState(true);
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const iframeRef = useRef(null);

  useEffect(() => { const t = setTimeout(() => setLoading(false), 2000); return () => clearTimeout(t); }, []);

  const refresh = useCallback(() => {
    setLoading(true);
    if (iframeRef.current) {
      try { iframeRef.current.contentWindow.location.reload(); }
      catch { iframeRef.current.src = PROXY + '?t=' + Date.now(); }
    }
    setTimeout(() => setLoading(false), 2000);
  }, []);

  const sanitizePhone = (input) => {
    let n = input.replace(/[\s\-\(\)\.]/g, '');
    if (n.startsWith('+')) n = n.substring(1);
    if (n.startsWith('00')) n = n.substring(2);
    if (n.startsWith('0') && n.length === 11) n = '90' + n.substring(1);
    return n.replace(/\D/g, '');
  };

  const startChat = () => {
    setPhoneError('');
    const num = sanitizePhone(phoneInput);
    if (num.length < 10 || num.length > 15) { setPhoneError('Geçersiz numara'); return; }
    if (iframeRef.current) iframeRef.current.src = `https://wa.me/${num}`;
    setPhoneInput('');
  };

  if (desktopMode) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flexShrink: 0, padding: '8px 16px', borderBottom: '1px solid hsl(var(--border))' }} className="bg-card flex items-center justify-between">
          <div className="flex items-center gap-2"><MessageCircle className="w-4 h-4 text-green-500" /><span className="text-sm font-semibold text-foreground">WhatsApp</span></div>
          <Button variant="ghost" size="sm" className="h-7 text-xs rounded-lg" onClick={async () => { try { await window.electronAPI.whatsapp.refresh(); } catch {} }}><RefreshCw className="w-3 h-3 mr-1" /> Yenile</Button>
        </div>
        <div style={{ flex: 1 }} />
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* Header */}
      <div style={{ flexShrink: 0, padding: '4px 8px', borderBottom: '1px solid hsl(var(--border))', zIndex: 10 }} className="bg-card flex items-center justify-between">
        <div className="flex items-center gap-1">
          <MessageCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
          <span className="text-xs font-semibold text-foreground">WhatsApp</span>
          {loading && <RefreshCw className="w-3 h-3 animate-spin text-green-500" />}
        </div>
        <Button variant="ghost" size="sm" className="h-6 text-[10px] rounded-lg px-2" onClick={refresh}><RefreshCw className="w-3 h-3 mr-0.5" /> Yenile</Button>
      </div>

      {/* Phone input */}
      <div style={{ flexShrink: 0, padding: '3px 8px', borderBottom: '1px solid hsl(var(--border))' }} className="bg-card">
        <div className="flex gap-1">
          <div className="relative flex-1">
            <Smartphone className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
            <input value={phoneInput} onChange={e => { setPhoneInput(e.target.value); setPhoneError(''); }}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); startChat(); } }}
              placeholder="Numara (+90...)"
              className="w-full pl-6 pr-2 py-1 rounded-lg bg-background border border-border/70 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-green-500/40" />
          </div>
          <Button size="sm" className="rounded-lg h-6 text-[10px] bg-green-600 hover:bg-green-700 text-white px-2 flex-shrink-0" onClick={startChat} disabled={!phoneInput.trim()}>
            <MessageCircle className="w-3 h-3" />
          </Button>
        </div>
        {phoneError && <p className="text-[9px] text-destructive mt-0.5">{phoneError}</p>}
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ position: 'absolute', top: 52, left: 0, right: 0, bottom: 0, zIndex: 5, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'hsl(222,22%,7%)', pointerEvents: 'none' }}>
          <RefreshCw className="w-8 h-8 animate-spin text-green-500 mb-3" />
          <p className="text-sm font-medium text-foreground">WhatsApp Web yükleniyor...</p>
        </div>
      )}

      {/* iframe — full width/height, no black gap */}
      <iframe ref={iframeRef} src={PROXY} onLoad={() => setLoading(false)}
        title="WhatsApp Web" allow="camera; microphone; clipboard-read; clipboard-write"
        style={{
          flex: 1,
          width: '100%',
          height: '100%',
          border: 'none',
          display: 'block',
          minHeight: 0,
        }} />
    </div>
  );
}
